import {defineMessages, FormattedMessage, intlShape, injectIntl} from 'react-intl';
import PropTypes from 'prop-types';
import React from 'react';
import Box from '../box/box.jsx';
import Modal from '../../containers/modal.jsx';
import Spinner from '../spinner/spinner.jsx';
import styles from './ccw-extension-modal.css';

const messages = defineMessages({
    title: {
        defaultMessage: 'Load CCW Extension',
        description: 'Title of CCW extension loader modal',
        id: 'tw.ccwExtensionModal.title'
    },
    description: {
        defaultMessage: 'Load the extension from the Cocrea World, which can be obtained from https://assets.ccw.site/extensions.',
        description: 'Description of CCW extension loader modal',
        id: 'tw.ccwExtensionModal.description'
    },
    prompt: {
        defaultMessage: 'Enter CCW extension ID：',
        description: 'Label that appears when loading a CCW extension by ID',
        id: 'tw.ccwExtensionModal.prompt'
    },
    lookup: {
        defaultMessage: 'Fetch',
        description: 'Button to fetch CCW extension metadata',
        id: 'tw.ccwExtensionModal.lookup'
    },
    publisher: {
        defaultMessage: 'Author',
        description: 'Publisher label in CCW extension loader',
        id: 'tw.ccwExtensionModal.publisher'
    },
    version: {
        defaultMessage: 'Version',
        description: 'Version selector label in CCW extension loader',
        id: 'tw.ccwExtensionModal.version'
    },
    latest: {
        defaultMessage: 'Latest version',
        description: 'Latest version marker in CCW extension loader',
        id: 'tw.ccwExtensionModal.latest'
    },
    asset: {
        defaultMessage: 'Asset',
        description: 'Asset URL label in CCW extension loader',
        id: 'tw.ccwExtensionModal.asset'
    },
    cancel: {
        defaultMessage: 'Cancel',
        description: 'Button that cancels loading a CCW extension',
        id: 'tw.ccwExtensionModal.cancel'
    },
    confirm: {
        defaultMessage: 'Confirm',
        description: 'Button that confirms loading a CCW extension',
        id: 'tw.ccwExtensionModal.confirm'
    }
});

const CCWExtensionModal = props => {
    const versions = Array.isArray(props.metadata?.versions) ? props.metadata.versions : [];
    const selectedVersion = versions[props.selectedVersionIndex] || versions[0];
    const publisher = props.metadata?.publisher?.nickname || props.metadata?.publisherId || 'Unknown';
    return (
        <Modal
            className={styles.modalContent}
            onRequestClose={props.onClose}
            contentLabel={props.intl.formatMessage(messages.title)}
            id="ccwExtensionModal"
        >
            <Box className={styles.body}>
                <h3 className={styles.title}>
                    <FormattedMessage {...messages.title} />
                </h3>
                <p><FormattedMessage {...messages.description} /></p>
                <p><FormattedMessage {...messages.prompt} /></p>
                <div className={styles.inputRow}>
                    <input
                        type="text"
                        className={styles.urlInput}
                        value={props.extensionId}
                        onChange={props.onChangeExtensionId}
                        onKeyDown={props.onKeyDown}
                        placeholder="Input Extension ID"
                        disabled={props.loading}
                        autoFocus
                    />
                    <button
                        className={styles.lookupButton}
                        onClick={props.onLookup}
                        disabled={props.loading || !props.extensionId.trim()}
                    >
                        {props.loading ? <Spinner small /> : <FormattedMessage {...messages.lookup} />}
                    </button>
                </div>
                {props.error ? <div className={styles.errorMessage}>{props.error}</div> : null}
                {props.metadata ? (
                    <div className={styles.extensionInfo}>
                        <div className={styles.infoHeader}>
                            {props.metadata.cover ? (
                                <img className={styles.cover} src={props.metadata.cover} draggable={false} />
                            ) : null}
                            <div>
                                <div className={styles.name}>{props.metadata.name || props.metadata.eid}</div>
                                <div className={styles.meta}>
                                    <FormattedMessage {...messages.publisher} values={{publisher}} />
                                </div>
                            </div>
                        </div>
                        {props.metadata.description ? <p>{props.metadata.description}</p> : null}
                        <p><FormattedMessage {...messages.version} /></p>
                        <select
                            className={styles.versionSelect}
                            value={props.selectedVersionIndex}
                            onChange={props.onChangeVersion}
                        >
                            {versions.map((version, index) => (
                                <option key={version.id || version.version || index} value={index}>
                                    {version.version || `#${index + 1}`}{index === 0 ? ` (${props.intl.formatMessage(messages.latest)})` : ''}
                                </option>
                            ))}
                        </select>
                        {selectedVersion?.assetUri ? (
                            <div className={styles.assetUri}>
                                <FormattedMessage {...messages.asset} values={{asset: selectedVersion.assetUri}} />
                            </div>
                        ) : null}
                    </div>
                ) : null}
                <div className={styles.buttonRow}>
                    <button className={styles.cancelButton} onClick={props.onClose} disabled={props.loading}>
                        <FormattedMessage {...messages.cancel} />
                    </button>
                    <button
                        className={styles.loadButton}
                        onClick={props.onConfirm}
                        disabled={props.loading || !selectedVersion?.assetUri}
                    >
                        <FormattedMessage {...messages.confirm} />
                    </button>
                </div>
            </Box>
        </Modal>
    );
};

CCWExtensionModal.propTypes = {
    error: PropTypes.string,
    extensionId: PropTypes.string.isRequired,
    intl: intlShape,
    loading: PropTypes.bool.isRequired,
    metadata: PropTypes.object,
    onChangeExtensionId: PropTypes.func.isRequired,
    onChangeVersion: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    onKeyDown: PropTypes.func.isRequired,
    onLookup: PropTypes.func.isRequired,
    selectedVersionIndex: PropTypes.number.isRequired
};

export default injectIntl(CCWExtensionModal);
